import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import Reveal from "@/components/Reveal";
import { FaqList } from "@/pages/Faq";
import { ArrowRight, Check, Anchor } from "lucide-react";

const STORY_IMG =
  "https://images.unsplash.com/photo-1500375592092-40eb2168fd21?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80";

export default function WhyBevela() {
  const { t } = useLang();
  const w = t.whyBevela;
  const a = t.about;
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div data-testid="why-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <Reveal>
          <p className="eyebrow">{w.hero.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B192C] leading-[1.05] max-w-3xl" data-testid="why-title">{w.hero.title}</h1>
          <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-2xl">{w.hero.subtitle}</p>
        </Reveal>
      </section>

      {/* #por-que */}
      <section id="por-que" className="scroll-mt-24 border-t border-slate-100 bg-[#F8F9FA] py-16 lg:py-24" data-testid="why-section-por-que">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="eyebrow">{w.why.anchorTitle}</p></Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-10">
            {[w.why.what, w.why.problem, w.why.coownership].map((c, i) => (
              <Reveal key={c.h} delay={i * 100} className="border-t border-[#C2A878]/60 pt-6">
                <h3 className="font-serif text-2xl text-[#0B192C]">{c.h}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{c.p}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-12">
            <Reveal>
              <h3 className="font-serif text-3xl text-[#0B192C]">{w.why.advantages.h}</h3>
              <ul className="mt-6 space-y-4">
                {w.why.advantages.items.map((it) => (
                  <li key={it} className="flex gap-3 text-slate-600">
                    <Check className="h-5 w-5 text-[#C2A878] shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={150}>
              <h3 className="font-serif text-3xl text-[#0B192C]">{w.why.how.h}</h3>
              <div className="mt-6 space-y-6">
                {w.why.how.steps.map((s, i) => (
                  <div key={s.t} className="flex gap-4">
                    <span className="font-serif text-2xl text-[#C2A878] w-8 shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-display text-[#0B192C]">{s.t}</p>
                      <p className="text-sm text-slate-600 leading-relaxed mt-1">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* #propietarios */}
      <section id="propietarios" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="why-section-propietarios">
        <Reveal>
          <p className="eyebrow">{w.owners.anchorTitle}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-[#0B192C] max-w-2xl">{w.owners.intro.h}</h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">{w.owners.intro.p}</p>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {w.owners.items.map((it, i) => (
            <Reveal key={it.h} delay={i * 80} className="rounded-[24px] border border-slate-100 p-8">
              <h3 className="font-serif text-xl text-[#0B192C]">{it.h}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed text-sm">{it.p}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={150} className="mt-12">
          <Link to="/acceso" className="btn-primary" data-testid="why-owners-cta">{w.owners.cta} <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </section>

      {/* #quienes-somos */}
      <section id="quienes-somos" className="scroll-mt-24 border-t border-slate-100 bg-[#0B192C] text-white py-16 lg:py-24" data-testid="why-section-quienes-somos">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="eyebrow text-[#C2A878]">{w.about.anchorTitle}</p></Reveal>
          <div className="mt-8 grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight">{a.title}</h2>
              <p className="mt-6 text-white/75 leading-relaxed max-w-xl">{a.body1}</p>
              <p className="mt-4 text-white/75 leading-relaxed max-w-xl">{a.body2}</p>
            </Reveal>
            <Reveal delay={150} className="lg:col-span-5">
              <div className="aspect-[4/5] w-full overflow-hidden rounded-[24px] border border-white/10">
                <img src={STORY_IMG} alt="" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-10">
            <Reveal className="border-t border-[#C2A878]/50 pt-6">
              <h3 className="font-serif text-2xl">{w.about.philosophy.h}</h3>
              <p className="mt-3 text-white/75 leading-relaxed">{w.about.philosophy.p}</p>
            </Reveal>
            <Reveal delay={100} className="border-t border-[#C2A878]/50 pt-6">
              <h3 className="font-serif text-2xl">{w.about.project.h}</h3>
              <p className="mt-3 text-white/75 leading-relaxed">{w.about.project.p}</p>
            </Reveal>
          </div>

          <Reveal className="mt-16"><p className="eyebrow text-[#C2A878]">{a.teamTitle}</p></Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            {a.team.map((m, i) => (
              <Reveal key={m.name} delay={i * 100} className="rounded-[24px] border border-white/10 p-8 bg-white/[0.03]">
                <div className="h-14 w-14 rounded-full bg-[#C2A878] text-[#0B192C] font-serif text-xl flex items-center justify-center">{m.name.charAt(0)}</div>
                <h3 className="font-serif text-2xl mt-6">{m.name}</h3>
                <p className="font-display text-xs tracking-[0.2em] uppercase text-[#C2A878] mt-1">{m.role}</p>
                <p className="mt-4 text-white/70 leading-relaxed text-sm">{m.bio}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* #faq */}
      <section id="faq" className="scroll-mt-24 max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="why-section-faq">
        <Reveal>
          <p className="eyebrow">{w.faqTitle}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-[#0B192C]">{t.faq.title}</h2>
        </Reveal>
        <div className="mt-10">
          <FaqList items={t.faq.items} testIdPrefix="why-faq" />
        </div>
        <Reveal delay={150} className="mt-12">
          <Link to="/acceso" className="btn-primary" data-testid="why-faq-cta">{t.nav.access} <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 flex items-center gap-2 text-slate-400">
        <Anchor className="h-4 w-4" /> <span className="font-display tracking-wide text-sm">Bevela. Vívela.</span>
      </div>
    </div>
  );
}
